import { useCallback, useEffect, useState } from "react";
import { FiCheckCircle } from "react-icons/fi";
import { TbShoppingBag, TbUserCircle } from "react-icons/tb";
import { useNavigate, useParams } from "react-router-dom";
import logoImg from "~/assets/img/gpaLogo.png";
import { Button } from "~/components/Button";
import { LoginForm } from "~/forms/PersonalTrainer/Login";
import { PersonalTrainerSignUpForm } from "~/forms/PersonalTrainerSignUpForm";
import { authenticationAtom } from "~/modules/authentication/data";
import type { PersonalTrainerSignInDTO } from "~/modules/authentication/schema";
import {
	authenticatePersonalTrainer,
	signOut,
} from "~/modules/authentication/services";
import type { PersonalTrainerSingUpDTO } from "~/modules/personalTrainer/schema";
import { signUpPersonalTrainer } from "~/modules/personalTrainer/services";
import { insertToast } from "~/modules/toasts/services";
import { useAtom } from "~/observers/jotai";
import { Frame } from "~/pages/open/SubscriptionsCheckout/components/Frame";
import { api } from "~/utils/api";

type InviteDetails = {
	token: string;
	plan: {
		name: string;
		licensesAmount: number;
	};
};

export function CourtesyCheckout() {
	const { token } = useParams<{ token: string }>();
	const [session] = useAtom(authenticationAtom);
	const navigate = useNavigate();

	const [loading, setLoading] = useState(true);
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [inviteDetails, setInviteDetails] = useState<InviteDetails | null>(
		null,
	);
	const [success, setSuccess] = useState(false);
	const [formType, setFormType] = useState<"login" | "signup">("login");

	const fetchInvite = useCallback(async () => {
		try {
			setLoading(true);
			const res = await api.get(`/sales/invites/${token}`);
			setInviteDetails(res.data);
		} catch (err: any) {
			insertToast({
				title: "Convite inválido",
				message:
					err.response?.data?.message ||
					"Não foi possível carregar este convite de cortesia.",
				type: "error",
			});
			navigate("/");
		} finally {
			setLoading(false);
		}
	}, [token, navigate]);

	useEffect(() => {
		fetchInvite();
	}, [fetchInvite]);

	const handleCheckout = useCallback(async () => {
		try {
			setIsSubmitting(true);
			await api.post("/sales/courtesy-checkout", { courtesyToken: token });
			setSuccess(true);
		} catch (err: any) {
			insertToast({
				title: "Erro ao resgatar",
				message:
					err.response?.data?.message ||
					"Ocorreu um erro ao processar sua cortesia.",
				type: "error",
			});
		} finally {
			setIsSubmitting(false);
		}
	}, [token]);

	const handleSignIn = useCallback(
		async (payload: PersonalTrainerSignInDTO) => {
			if (payload) {
				await authenticatePersonalTrainer(payload);
			}
		},
		[],
	);

	const handleSignUp = useCallback(
		async (payload: PersonalTrainerSingUpDTO) => {
			const data = await signUpPersonalTrainer(payload);
			if (data) {
				await authenticatePersonalTrainer({
					documentNumber: data.documentNumber,
					password: payload.password!,
				});
			}
		},
		[],
	);

	if (loading) {
		return (
			<div className="flex items-center justify-center min-h-screen bg-gpa-gray-50">
				<p>Carregando...</p>
			</div>
		);
	}

	if (success) {
		return (
			<div className="flex flex-col items-center justify-center min-h-screen bg-gpa-gray-50 p-4">
				<div className="flex flex-col items-center bg-white p-8 rounded-xl shadow gap-4 text-center max-w-md w-full">
					<FiCheckCircle size={64} className="text-gpa-green" />
					<h1 className="text-2xl font-bold">Cortesia resgatada!</h1>
					<p>
						O plano cortesia <strong>{inviteDetails?.plan.name}</strong> foi
						ativado na sua conta. Você já tem acesso às licenças!
					</p>
					<Button onClick={() => navigate("/app/painel")} className="mt-4">
						Acessar Painel
					</Button>
				</div>
			</div>
		);
	}

	return (
		<div className="flex flex-col items-center gap-5 w-full bg-gpa-gray-50 pt-8 pb-10 min-h-screen">
			<img src={logoImg} alt="GPA" />
			<div className="flex w-full max-h-fit flex-1 flex-col-reverse md:flex-row p-5 max-w-6xl mx-auto gap-8 self-start">
				<div className="relative flex flex-col flex-1 gap-4">
					<Frame
						title="Meus dados"
						icon={<TbUserCircle size={24} className="text-gpa-blue-500" />}
						open={!session}
						editable={!!session}
						onEdit={() => {}}
					>
						{!session ? (
							<div className="flex flex-col gap-3 items-end">
								<div>
									{formType === "login" ? (
										<Button mode="blank" onClick={() => setFormType("signup")}>
											Não tenho cadastro no GPA STÚDIO PRO
										</Button>
									) : (
										<Button mode="blank" onClick={() => setFormType("login")}>
											Voltar ao login
										</Button>
									)}
								</div>
								<div className="w-full">
									{formType === "login" ? (
										<LoginForm
											buttonLabel="PRÓXIMO PASSO"
											onSubmit={handleSignIn}
										/>
									) : (
										<PersonalTrainerSignUpForm
											buttonLabel="PRÓXIMO PASSO"
											onSubmit={handleSignUp}
										/>
									)}
								</div>
							</div>
						) : (
							<div className="flex flex-col items-center gap-4 py-4">
								<h2 className="text-xl font-semibold">
									Olá, {session.user.name.split(" ")[0]}
								</h2>
								<div className="h-[1px] bg-slate-300 w-full"></div>
								<p className="text-center">
									Identificamos seu acesso na plataforma. <br /> Esta assinatura
									de cortesia será vinculada à sua conta.
								</p>
								<div className="flex gap-3 w-full max-w-[400px] my-2">
									<Button
										className="flex-1"
										mode="blank"
										color="error"
										onClick={signOut}
									>
										Trocar Acesso
									</Button>
								</div>
							</div>
						)}
					</Frame>

					{session && (
						<Frame
							title="Finalizar Resgate"
							icon={<FiCheckCircle size={24} className="text-gpa-blue-500" />}
							open={true}
						>
							<div className="flex flex-col gap-4 text-center mt-2 px-2">
								<p className="text-sm text-slate-500">
									Confirme abaixo para ativar a sua assinatura de cortesia
									vinculada à sua conta. Não é necessário cartão de crédito.
								</p>
								<Button
									loading={isSubmitting}
									onClick={handleCheckout}
									className="w-full"
								>
									Resgatar Cortesia
								</Button>
							</div>
						</Frame>
					)}
				</div>

				<div className="self-start w-full md:sticky md:top-10 flex-1 md:max-w-[400px]">
					<Frame
						title="Resumo"
						icon={<TbShoppingBag size={24} className="text-gpa-blue-500" />}
						open
					>
						{inviteDetails ? (
							<div className="flex flex-col gap-4 px-2 py-4">
								<div className="flex items-center justify-between pb-4 border-b border-gray-200">
									<div className="flex flex-col">
										<span className="font-semibold text-lg text-slate-700">
											{inviteDetails.plan.name}
										</span>
										<span className="text-sm text-gray-500">
											{inviteDetails.plan.licensesAmount} licenças inclusas
										</span>
									</div>
									<span className="text-xl font-bold text-gpa-green">
										GRÁTIS
									</span>
								</div>
							</div>
						) : (
							<div className="flex items-center justify-center p-4 text-gray-400">
								Carregando dados da cortesia...
							</div>
						)}
					</Frame>
				</div>
			</div>
		</div>
	);
}
